import { useState, useEffect, useCallback, FormEvent, ChangeEvent } from 'react';
import {
  Settings, Puzzle, Package, BadgeCheck, CircleX, Earth, Tag, Cpu,
  Trash2, Users, Plus, X, Clipboard, Save, Star, ArrowRight, Search,
  Map as MapIcon, Power, Activity, AlertCircle, Check, RotateCw,
  Loader2, Globe,
} from 'lucide-react';
import Flag from 'react-world-flags';
import { StatsPanel } from '../StatsPanel';

// =========================================================================
// Shared types
// =========================================================================

export interface Plan {
  id: string;
  label: string;
  description: string;
  price: number;
  location?: string;
  binary?: string;
}

// Mapeo de códigos de ubicación de Hetzner a nombres amigables.
const LOCATION_INFO: Record<string, { name: string; description: string; flag: string }> = {
  nbg1: { name: 'Alemania', description: 'Núremberg, Europa Central', flag: 'DE' },
  ash:  { name: 'Estados Unidos', description: 'Ashburn, VA · Costa Este', flag: 'US' },
  fsn1: { name: 'Alemania', description: 'Falkenstein, Europa Central', flag: 'DE' },
  hel1: { name: 'Finlandia', description: 'Helsinki, Europa del Norte', flag: 'FI' },
  hil:  { name: 'Estados Unidos', description: 'Hillsboro, OR · Costa Oeste', flag: 'US' },
};

function getLocationInfo(code?: string) {
  if (!code) return { name: code ?? '—', description: '', flag: '' };
  return LOCATION_INFO[code] || { name: code.toUpperCase(), description: code, flag: '' };
}

function LocationFlag({ code }: { code: string }) {
  if (!code) return <Globe size={14} />;
  return (
    <Flag
      code={code}
      style={{ width: '1em', height: '1em', verticalAlign: '-0.125em', borderRadius: '2px' }}
    />
  );
}

export interface DownloadedItem {
  filename: string;
  url: string;
}

export interface ConfigFile {
  file: string;
  name: string;
  description: string;
  type: string;
  content: string;
}

export interface ConfigData {
  serverId: string;
  serverName: string;
  gameType: string;
  version: string;
  serverActive?: boolean;
  source?: 'server' | 'r2';
  configs: ConfigFile[];
}

export interface ServerLite {
  id: string;
  name: string;
  type: string;
  version: string;
  active?: boolean;
  ftp_password?: string;
  game?: string;
}

// =========================================================================
// Generic modal shell (overlay + container + optional header)
// =========================================================================

interface ShellProps {
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export function ModalShell({ onClose, size = 'md', children }: ShellProps) {
  return (
    <div className="sp-modal-overlay" onClick={onClose}>
      <div
        className={`sp-modal sp-modal--${size}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

interface HeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClose: () => void;
}

export function ModalHeader({ icon, title, subtitle, onClose }: HeaderProps) {
  return (
    <div className="sp-modal-header">
      <div className="sp-modal-header-left">
        <div className="sp-modal-header-icon">{icon}</div>
        <div>
          <h2 className="sp-modal-title">{title}</h2>
          {subtitle && <p className="sp-modal-subtitle">{subtitle}</p>}
        </div>
      </div>
      <button type="button" className="sp-modal-close" onClick={onClose} aria-label="Cerrar">
        <X size={18} />
      </button>
    </div>
  );
}

// =========================================================================
// PlanModal
// =========================================================================

interface PlanModalProps {
  plans: Plan[];
  loading: boolean;
  currentPlanId?: string;
  onSelect: (plan: Plan) => void;
  onClose: () => void;
}

export function PlanModal({ plans, loading, currentPlanId, onSelect, onClose }: PlanModalProps) {
  const [activeLocation, setActiveLocation] = useState<string | null>(null);

  // Ubicaciones únicas que devuelve la API, en orden estable.
  const availableLocations = Array.from(
    new Set(plans.map((p) => p.location).filter((l): l is string => Boolean(l)))
  );

  // Auto-selecciona la ubicación del plan actual cuando los planes terminan de cargar.
  // Si el usuario no tiene plan actual, cae a la primera ubicación disponible.
  useEffect(() => {
    if (activeLocation || plans.length === 0) return;
    if (currentPlanId) {
      const currentPlan = plans.find((p) => p.id === currentPlanId);
      if (currentPlan?.location) {
        setActiveLocation(currentPlan.location);
        return;
      }
    }
    if (availableLocations.length > 0) setActiveLocation(availableLocations[0]);
    // availableLocations se deriva de `plans`, no necesita estar en deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, currentPlanId, activeLocation]);

  const filteredPlans = activeLocation
    ? plans.filter((p) => p.location === activeLocation)
    : [];
  const activeLocationInfo = getLocationInfo(activeLocation ?? undefined);

  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Settings size={18} />}
        title="Seleccionar Plan"
        subtitle={
          activeLocation
            ? `Planes disponibles en ${activeLocationInfo.name}`
            : 'Elige el plan para tu servidor'
        }
        onClose={onClose}
      />
      <div className="sp-modal-body">
        {loading ? (
          <div className="sp-modal-body--center" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: '0.75rem', color: 'var(--text-secondary)' }}>
              Cargando planes…
            </p>
          </div>
        ) : availableLocations.length === 0 ? (
          <div className="sp-empty">
            <span className="sp-empty-icon">📦</span>
            <p>No hay planes disponibles.</p>
          </div>
        ) : (
          <>
            {/* Nav de ubicaciones: siempre visible para cambiar entre regiones */}
            <div className="sp-location-tabs" role="tablist" aria-label="Ubicaciones">
              {availableLocations.map((locCode) => {
                const info = getLocationInfo(locCode);
                const isActive = locCode === activeLocation;
                return (
                  <button
                    key={locCode}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`sp-location-tab ${isActive ? 'is-active' : ''}`}
                    onClick={() => setActiveLocation(locCode)}
                  >
                    <span className="sp-location-tab-flag" aria-hidden="true"><LocationFlag code={info.flag} /></span>
                    <span className="sp-location-tab-name">{info.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Planes de la ubicación activa */}
            {filteredPlans.length === 0 ? (
              <div className="sp-empty">
                <span className="sp-empty-icon">📦</span>
                <p>No hay planes disponibles para esta ubicación.</p>
              </div>
            ) : (
              <div className="sp-plan-grid">
                {filteredPlans.map((plan) => {
                  const isCurrent = plan.id === currentPlanId;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      className={`sp-plan-card ${isCurrent ? 'is-current' : ''}`}
                      onClick={() => onSelect(plan)}
                    >
                      <div className="sp-plan-card-head">
                        <span className="sp-plan-card-label">{plan.label}</span>
                        {isCurrent && <span className="sp-plan-card-current">actual</span>}
                      </div>
                      <p className="sp-plan-card-desc">{plan.description}</p>
                      <div className="sp-plan-card-price">
                        <span>${plan.price.toFixed(3)}</span>
                        <span className="sp-plan-card-unit">/hora</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}

// =========================================================================
// AddonsModal (consolidates Plugins + Mods)
// =========================================================================

export type AddonKind = 'plugin' | 'mod';

interface AddonsModalProps {
  kind: AddonKind;
  installed: DownloadedItem[];
  searchResults: any[];
  popularItems: any[];
  loadingInstalled: boolean;
  searching: boolean;
  loadingPopular: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  searchQuery: string;
  installingId: string | null;
  uninstallingName: string | null;
  onSearchQueryChange: (q: string) => void;
  onSearch: () => void;
  onLoadMore: () => void;
  onInstall: (id: string) => void;
  onUninstall: (name: string) => void;
  onClose: () => void;
  serverType: string;
  serverVersion: string;
}

export function AddonsModal({
  kind,
  installed,
  searchResults,
  popularItems,
  loadingInstalled,
  searching,
  loadingPopular,
  hasMore,
  loadingMore,
  searchQuery,
  installingId,
  uninstallingName,
  onSearchQueryChange,
  onSearch,
  onLoadMore,
  onInstall,
  onUninstall,
  onClose,
  serverType,
  serverVersion,
}: AddonsModalProps) {
  const isPlugin = kind === 'plugin';
  const label = isPlugin ? 'Plugin' : 'Mod';
  const Icon = isPlugin ? Puzzle : Package;
  const subtitle = isPlugin
    ? 'Busca e instala plugins de Modrinth'
    : 'Busca e instala mods de Modrinth';

  return (
    <ModalShell onClose={onClose} size="lg">
      <ModalHeader
        icon={<Icon size={18} />}
        title={`Gestor de ${isPlugin ? 'Plugins' : 'Mods'}`}
        subtitle={subtitle}
        onClose={onClose}
      />

      <div className="sp-modal-body sp-modal-body--flush sp-addons-body">
        <div className="sp-addons-toolbar">
          <div className="sp-addons-meta">
            <span className="sp-pill sp-pill--mono">{serverType.toUpperCase()}</span>
            <span className="sp-pill sp-pill--mono">v{serverVersion}</span>
          </div>
          <div className="sp-form-row" style={{ flex: 1, maxWidth: '420px' }}>
            <input
              type="text"
              className="sp-input"
              placeholder={`Buscar ${label.toLowerCase()}…`}
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchQueryChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
            />
            <button type="button" className="btn btn-primary" onClick={onSearch} disabled={searching || !searchQuery.trim()}>
              <Search size={14} /> {searching ? '…' : 'Buscar'}
            </button>
          </div>
        </div>

        <div className="sp-addons-grid">
          {/* Left: installed */}
          <div className="sp-addons-col">
            <div className="sp-addons-col-header">
              <h3>Instalados</h3>
              <span className="sp-pill sp-pill--mono">{installed.length}</span>
            </div>
            {loadingInstalled ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
            ) : installed.length === 0 ? (
              <div className="sp-empty">
                <span className="sp-empty-icon">📭</span>
                <p>No hay {isPlugin ? 'plugins' : 'mods'} instalados</p>
              </div>
            ) : (
              <ul className="sp-addons-list">
                {installed.map((item) => (
                  <li key={item.filename} className="sp-addon-row">
                    <div className="sp-addon-icon"><Icon size={16} /></div>
                    <span className="sp-addon-name">{item.filename}</span>
                    <button
                      type="button"
                      className="sp-copy-btn"
                      disabled={uninstallingName === item.filename}
                      onClick={() => onUninstall(item.filename)}
                      title={`Eliminar ${label.toLowerCase()}`}
                      aria-label={`Eliminar ${label.toLowerCase()} ${item.filename}`}
                    >
                      {uninstallingName === item.filename ? <span className="spinner-small" /> : <Trash2 size={14} />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: search results or popular */}
          <div className="sp-addons-col">
            <div className="sp-addons-col-header">
              <h3>
                {searchQuery ? 'Resultados' : `${isPlugin ? 'Plugins' : 'Mods'} populares`}
              </h3>
            </div>
            {searchQuery ? (
              searching && searchResults.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
              ) : searchResults.length === 0 ? (
                <div className="sp-empty">
                  <span className="sp-empty-icon">🔍</span>
                  <p>Sin resultados para “{searchQuery}”</p>
                </div>
              ) : (
                <ul className="sp-addons-list">
                  {searchResults.map((item) => (
                    <li key={item.project_id} className="sp-addon-row">
                      {item.icon_url ? (
                        <img src={item.icon_url} alt="" className="sp-addon-icon-img" />
                      ) : (
                        <div className="sp-addon-icon"><Icon size={16} /></div>
                      )}
                      <div className="sp-addon-info">
                        <span className="sp-addon-name">{item.title}</span>
                        {item.description && (
                          <span className="sp-addon-desc">{item.description.slice(0, 60)}…</span>
                        )}
                      </div>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="sp-copy-btn" title="Ver en Modrinth">
                        <ArrowRight size={14} />
                      </a>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={installingId === item.project_id}
                        onClick={() => onInstall(item.project_id)}
                      >
                        {installingId === item.project_id ? <span className="spinner-small" /> : 'Instalar'}
                      </button>
                    </li>
                  ))}
                  {hasMore && (
                    <li style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={loadingMore} onClick={onLoadMore}>
                        {loadingMore ? 'Cargando…' : 'Cargar más'}
                      </button>
                    </li>
                  )}
                </ul>
              )
            ) : loadingPopular ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
            ) : popularItems.length === 0 ? (
              <div className="sp-empty">
                <span className="sp-empty-icon">✨</span>
                <p>Busca {isPlugin ? 'plugins' : 'mods'} arriba</p>
              </div>
            ) : (
              <ul className="sp-addons-list">
                {popularItems.map((item) => (
                  <li key={item.project_id} className="sp-addon-row">
                    {item.icon_url ? (
                      <img src={item.icon_url} alt="" className="sp-addon-icon-img" />
                    ) : (
                      <div className="sp-addon-icon"><Icon size={16} /></div>
                    )}
                    <div className="sp-addon-info">
                      <span className="sp-addon-name">{item.title}</span>
                      {item.description && (
                        <span className="sp-addon-desc">{item.description.slice(0, 60)}…</span>
                      )}
                    </div>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="sp-copy-btn" title="Ver en Modrinth">
                      <ArrowRight size={14} />
                    </a>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={installingId === item.project_id}
                      onClick={() => onInstall(item.project_id)}
                    >
                      {installingId === item.project_id ? <span className="spinner-small" /> : 'Instalar'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// PlayerListModal (consolidates Whitelist + Blacklist)
// =========================================================================

export type PlayerListKind = 'whitelist' | 'blacklist';

interface PlayerListModalProps {
  kind: PlayerListKind;
  players: string[];
  whitelistEnabled?: boolean;
  whitelistToggling?: boolean;
  loading: boolean;
  adding: boolean;
  removing: string | null;
  serverActive: boolean;
  onAdd: (name: string) => void;
  onRemove: (name: string) => void;
  onToggleWhitelist?: (enabled: boolean) => void;
  onClose: () => void;
}

export function PlayerListModal({
  kind,
  players,
  whitelistEnabled,
  whitelistToggling,
  loading,
  adding,
  removing,
  serverActive,
  onAdd,
  onRemove,
  onToggleWhitelist,
  onClose,
}: PlayerListModalProps) {
  const isWhitelist = kind === 'whitelist';
  const Icon = isWhitelist ? BadgeCheck : CircleX;
  const placeholder = isWhitelist ? 'Nombre del jugador' : 'Nombre del jugador a banear';
  const actionLabel = isWhitelist ? 'Añadir' : 'Banear';
  const removingLabel = isWhitelist ? 'Eliminando…' : 'Desbaneando…';
  const noPlayersLabel = isWhitelist ? 'No hay jugadores en la whitelist' : 'No hay jugadores baneados';

  const [input, setInput] = useState('');
  const handleAdd = () => {
    if (input.trim()) {
      onAdd(input.trim());
      setInput('');
    }
  };

  return (
    <ModalShell onClose={onClose} size="sm">
      <ModalHeader
        icon={<Icon size={18} />}
        title={isWhitelist ? 'Whitelist' : 'Blacklist'}
        subtitle={isWhitelist ? 'Jugadores con acceso permitido' : 'Jugadores baneados del servidor'}
        onClose={onClose}
      />
      <div className="sp-modal-body">
        {!serverActive && (
          <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, marginBottom: '1rem', borderBottom: 'none' }}>
            <span className="sp-banner-icon"><AlertCircle size={16} /></span>
            <span className="sp-banner-text">
              El servidor está <strong>apagado</strong>. No puedes modificar la lista.
            </span>
          </div>
        )}

        {isWhitelist && onToggleWhitelist && (
          <label className="sp-toggle">
            <input
              type="checkbox"
              checked={!!whitelistEnabled}
              onChange={(e) => onToggleWhitelist(e.target.checked)}
              disabled={whitelistToggling}
            />
            <span className="sp-toggle-track"><span className="sp-toggle-thumb" /></span>
            <span className="sp-toggle-label">
              {whitelistToggling ? 'Cambiando…' : whitelistEnabled ? 'Whitelist activada' : 'Whitelist desactivada'}
            </span>
          </label>
        )}

        {serverActive && (
          <div className="sp-form-row" style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              className="sp-input"
              placeholder={placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
            <button
              type="button"
              className={isWhitelist ? 'btn btn-primary' : 'btn btn-danger'}
              disabled={adding || !input.trim()}
              onClick={handleAdd}
            >
              {adding ? <span className="spinner-small" /> : <Plus size={14} />}
              {actionLabel}
            </button>
          </div>
        )}

        <div>
          <h4 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', fontFamily: 'var(--font-mono, monospace)' }}>
            {isWhitelist ? `Whitelist (${players.length})` : `Baneados (${players.length})`}
          </h4>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
          ) : players.length === 0 ? (
            <div className="sp-empty">
              <span className="sp-empty-icon"><Icon size={32} /></span>
              <p>{noPlayersLabel}</p>
            </div>
          ) : (
            <ul className="sp-player-list">
              {players.map((player) => (
                <li key={player} className="sp-player-row">
                  <span className="sp-player-name">
                    <Icon size={14} /> {player}
                  </span>
                  {serverActive && (
                    <button
                      type="button"
                      className="sp-copy-btn"
                      onClick={() => onRemove(player)}
                      disabled={removing === player}
                      title={isWhitelist ? 'Eliminar' : 'Desbanear'}
                      aria-label={isWhitelist ? `Eliminar ${player}` : `Desbanear ${player}`}
                    >
                      {removing === player ? <span className="spinner-small" /> : <Trash2 size={14} />}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// WorldsModal
// =========================================================================

interface WorldsModalProps {
  mapFile: File | null;
  uploadingMap: boolean;
  downloadMapLoading: boolean;
  hasPendingMap: boolean;
  uploadProgress: number;
  serverActive: boolean;
  game?: string;
  deleteWorldLoading: boolean;
  onSelectFile: (file: File) => void;
  onUpload: () => void;
  onDownload: () => void;
  onDeleteWorld: () => void;
  onClose: () => void;
}

export function WorldsModal({
  mapFile, uploadingMap, downloadMapLoading, hasPendingMap, uploadProgress,
  serverActive, game, deleteWorldLoading,
  onSelectFile, onUpload, onDownload, onDeleteWorld, onClose,
}: WorldsModalProps) {
  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Earth size={18} />}
        title="Mundos"
        subtitle="Sube o descarga el mundo de Minecraft"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        <div className="sp-worlds-grid">
          {/* Upload */}
          <div className="sp-worlds-card">
            <div className="sp-worlds-card-head">
              <span className="sp-worlds-icon">📤</span>
              <h3>Subir mapa</h3>
            </div>
            <p className="sp-worlds-desc">
              Sube un archivo .zip, .tar.gz, .tar o .gz con el mundo de Minecraft. Se aplicará al iniciar el servidor.
            </p>
            {!serverActive ? (
              <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, borderBottom: 'none' }}>
                <span className="sp-banner-icon"><AlertCircle size={16} /></span>
                <span className="sp-banner-text">El servidor debe estar <strong>encendido</strong></span>
              </div>
            ) : (
              <div className="sp-worlds-form">
                <input
                  type="file"
                  id="worlds-upload"
                  accept=".zip,.tar.gz,.tar,.gz"
                  className="sp-worlds-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onSelectFile(file);
                  }}
                />
                <label htmlFor="worlds-upload" className="sp-worlds-file-label">
                  {mapFile ? <>📁 {mapFile.name}</> : <>📂 Seleccionar archivo</>}
                </label>
                {mapFile && (
                  <p className="sp-worlds-file-size">
                    Tamaño: {(mapFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onUpload}
                  disabled={!mapFile || uploadingMap}
                >
                  {uploadingMap ? <><span className="spinner-small" /> Subiendo… {uploadProgress}%</> : <>🚀 Subir mapa</>}
                </button>
                {uploadingMap && (
                  <div className="sp-progress">
                    <div className="sp-progress-fill" style={{ '--sp-progress': uploadProgress / 100 } as React.CSSProperties} />
                    <span className="sp-progress-text">{uploadProgress}%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Download */}
          <div className="sp-worlds-card">
            <div className="sp-worlds-card-head">
              <span className="sp-worlds-icon">📥</span>
              <h3>Descargar mapa</h3>
            </div>
            <p className="sp-worlds-desc">
              Descarga el mapa actual del servidor en formato .tar.gz
            </p>
            {serverActive ? (
              <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, borderBottom: 'none' }}>
                <span className="sp-banner-icon"><AlertCircle size={16} /></span>
                <span className="sp-banner-text">El servidor debe estar <strong>apagado</strong></span>
              </div>
            ) : (
              <button type="button" className="btn btn-primary" onClick={onDownload} disabled={downloadMapLoading}>
                {downloadMapLoading ? <><span className="spinner-small" /> Descargando…</> : <>💾 Descargar mapa</>}
              </button>
            )}
          </div>
        </div>

        {/* Borrar mundo — solo Minecraft */}
        {game === 'Minecraft' && (
          <div className="sp-worlds-danger">
            <div className="sp-worlds-danger-head">
              <span className="sp-worlds-icon">⚠️</span>
              <h3>Borrar mundo</h3>
            </div>
            <p className="sp-worlds-desc">
              Elimina permanentemente la carpeta <code>world</code> del servidor.
              Esta acción <strong>no se puede deshacer</strong>.
            </p>
            {serverActive && (
              <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, borderBottom: 'none' }}>
                <span className="sp-banner-icon"><AlertCircle size={16} /></span>
                <span className="sp-banner-text">
                  El servidor está <strong>encendido</strong>. El mundo se regenerará al reiniciar.
                </span>
              </div>
            )}
            <button
              type="button"
              className="btn btn-danger"
              onClick={onDeleteWorld}
              disabled={deleteWorldLoading}
            >
              {deleteWorldLoading ? <><span className="spinner-small" /> Borrando…</> : <><Trash2 size={14} /> Borrar mundo</>}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// =========================================================================
// ChangeVersionModal
// =========================================================================

interface ChangeVersionModalProps {
  serverType: string;
  currentVersion: string;
  selectedVersion: string;
  searchQuery: string;
  changing: boolean;
  versions: Array<{ id: string; name: string; popular?: boolean }>;
  serverActive?: boolean;
  onSelectVersion: (v: string) => void;
  onSearchQueryChange: (q: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

export function ChangeVersionModal({
  serverType, currentVersion, selectedVersion, searchQuery, changing,
  versions, serverActive, onSelectVersion, onSearchQueryChange, onConfirm, onClose,
}: ChangeVersionModalProps) {
  const filtered = searchQuery.trim()
    ? versions.filter((v) => (v.name || v.id).toLowerCase().includes(searchQuery.toLowerCase()))
    : versions;

  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Tag size={18} />}
        title="Cambiar versión"
        subtitle="El cambio se aplicará al reiniciar el servidor"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        <div className="sp-version-flow">
          <div className="sp-version-card">
            <span className="sp-version-label">Actual</span>
            <span className="sp-version-value">{currentVersion || '—'}</span>
          </div>
          <ArrowRight size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div className={`sp-version-card ${selectedVersion ? 'is-set' : ''}`}>
            <span className="sp-version-label">Nueva</span>
            <span className="sp-version-value">{selectedVersion || '—'}</span>
          </div>
        </div>

        {serverActive && (
          <div className="sp-banner sp-banner--info" style={{ borderRadius: 8, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-icon"><Power size={16} /></span>
            <span className="sp-banner-text">Servidor <strong>encendido</strong>. El cambio surtirá efecto al reiniciar.</span>
          </div>
        )}

        <div className="sp-form-row" style={{ marginBottom: '1rem' }}>
          <Search size={16} style={{ position: 'absolute', marginLeft: '0.625rem', marginTop: '0.625rem', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="sp-input"
            placeholder="Buscar versión…"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            disabled={changing}
            style={{ paddingLeft: '2.25rem' }}
          />
        </div>

        {versions.length === 0 ? (
          <div className="sp-empty"><p>No hay versiones disponibles para este software.</p></div>
        ) : filtered.length === 0 ? (
          <div className="sp-empty"><p>Sin resultados para “{searchQuery}”.</p></div>
        ) : (
          <div className="sp-version-grid">
            {filtered.map((v) => {
              const isSelected = selectedVersion === v.id;
              const isCurrent = currentVersion === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onSelectVersion(v.id)}
                  disabled={changing}
                  className={`sp-version-chip ${isSelected ? 'is-selected' : ''} ${isCurrent ? 'is-current' : ''} ${v.popular ? 'is-popular' : ''}`}
                >
                  {v.popular && <Star size={11} />}
                  <span>{v.name || v.id}</span>
                  {isCurrent && <span className="sp-version-tag">actual</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={changing}>Cancelar</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onConfirm}
          disabled={changing || !selectedVersion || selectedVersion === currentVersion}
        >
          {changing ? <><span className="spinner-small" /> Guardando…</> : <><Save size={14} /> Guardar</>}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// ChangeTypeModal
// =========================================================================

interface ChangeTypeModalProps {
  currentType: string;
  selectedType: string;
  changing: boolean;
  serverActive?: boolean;
  onSelectType: (t: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const SOFTWARE_OPTIONS = [
  { id: 'paper',   name: 'Paper',   desc: 'Alto rendimiento, compatible con plugins Bukkit/Spigot.', tag: 'Plugins', color: 'blue' },
  { id: 'spigot',  name: 'Spigot',  desc: 'Servidor clásico compatible con plugins Bukkit.',       tag: 'Plugins', color: 'green' },
  { id: 'vanilla', name: 'Vanilla', desc: 'Servidor oficial de Minecraft, sin mods ni plugins.',   tag: 'Oficial', color: 'gray' },
  { id: 'fabric',  name: 'Fabric',  desc: 'Ligero y rápido, con soporte para mods de Fabric.',     tag: 'Mods',    color: 'amber' },
  { id: 'forge',   name: 'Forge',   desc: 'Soporte para mods de Minecraft Forge. Requiere más RAM.', tag: 'Mods', color: 'orange' },
];

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ChangeTypeModal({
  currentType, selectedType, changing, serverActive,
  onSelectType, onConfirm, onClose,
}: ChangeTypeModalProps) {
  const showRamWarning =
    (currentType === 'paper' || currentType === 'spigot') &&
    (selectedType === 'forge' || selectedType === 'fabric');

  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Cpu size={18} />}
        title="Cambiar software"
        subtitle="Selecciona el software con el que correrá el servidor"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        <div className="sp-version-flow">
          <div className="sp-version-card">
            <span className="sp-version-label">Actual</span>
            <span className="sp-version-value">{capitalize(currentType)}</span>
          </div>
          <ArrowRight size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <div className={`sp-version-card ${selectedType ? 'is-set' : ''}`}>
            <span className="sp-version-label">Nuevo</span>
            <span className="sp-version-value">{selectedType ? capitalize(selectedType) : '—'}</span>
          </div>
        </div>

        {serverActive && (
          <div className="sp-banner sp-banner--info" style={{ borderRadius: 8, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-icon"><Power size={16} /></span>
            <span className="sp-banner-text">Servidor <strong>encendido</strong>. El cambio surtirá efecto al reiniciar.</span>
          </div>
        )}

        {showRamWarning && (
          <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-icon"><AlertCircle size={16} /></span>
            <span className="sp-banner-text">
              Cambiar a <strong>{capitalize(selectedType)}</strong> requiere un plan intermedio o superior por el consumo de RAM.
            </span>
          </div>
        )}

        <div className="sp-type-grid">
          {SOFTWARE_OPTIONS.map((opt) => {
            const isSelected = selectedType === opt.id;
            const isCurrent = currentType === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onSelectType(opt.id)}
                disabled={changing}
                className={`sp-type-card sp-type-card--${opt.color} ${isSelected ? 'is-selected' : ''} ${isCurrent ? 'is-current' : ''}`}
              >
                <div className="sp-type-card-head">
                  <span className="sp-type-card-name">{opt.name}</span>
                  {isCurrent && <span className="sp-type-card-tag">actual</span>}
                </div>
                <p className="sp-type-card-desc">{opt.desc}</p>
                <span className="sp-type-card-badge">{opt.tag}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={changing}>Cancelar</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onConfirm}
          disabled={changing || !selectedType || selectedType === currentType}
        >
          {changing ? <><span className="spinner-small" /> Guardando…</> : <><Save size={14} /> Guardar</>}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// PropertiesModal (file editor)
// =========================================================================

interface PropertiesModalProps {
  configData: ConfigData | null;
  selectedConfigFile: ConfigFile | null;
  editedContent: string;
  saving: boolean;
  loading: boolean;
  mobileTab: 'files' | 'editor';
  isMobile: boolean;
  isContentModified: boolean;
  onSelectFile: (file: ConfigFile) => void;
  onChangeContent: (v: string) => void;
  onSave: () => void;
  onChangeMobileTab: (tab: 'files' | 'editor') => void;
  onClose: () => void;
  getFileIcon: (name: string) => string;
}

export function PropertiesModal({
  configData, selectedConfigFile, editedContent, saving, loading, mobileTab, isMobile,
  isContentModified, onSelectFile, onChangeContent, onSave, onChangeMobileTab, onClose, getFileIcon,
}: PropertiesModalProps) {
  return (
    <ModalShell onClose={onClose} size="lg">
      <ModalHeader
        icon={<Settings size={18} />}
        title="Archivos del servidor"
        subtitle="Edita la configuración de tu servidor"
        onClose={onClose}
      />
      <div className="sp-modal-body sp-modal-body--flush sp-props-body">
        {isMobile && (
          <div className="sp-props-tabs">
            <button
              type="button"
              className={`sp-props-tab ${mobileTab === 'files' ? 'active' : ''}`}
              onClick={() => onChangeMobileTab('files')}
            >📁 Archivos</button>
            <button
              type="button"
              className={`sp-props-tab ${mobileTab === 'editor' ? 'active' : ''}`}
              onClick={() => onChangeMobileTab('editor')}
              disabled={!selectedConfigFile}
            >✏️ Editor</button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
        ) : configData && configData.configs.length > 0 ? (
          <div className="sp-props-grid">
            <div className={`sp-props-sidebar ${isMobile && mobileTab === 'editor' ? 'mobile-hidden' : ''}`}>
              <div className="sp-props-sidebar-head">
                <h4>Archivos</h4>
                <span className="sp-pill sp-pill--mono">{configData.configs.length}</span>
              </div>
              {configData.source && (
                <p className="sp-props-meta">
                  {configData.source === 'server' ? '🟢 En vivo del servidor' : '☁️ Desde R2'}
                </p>
              )}
              {configData.serverActive !== undefined && (
                <p className="sp-props-meta">
                  {configData.serverActive ? '🟢 Servidor encendido' : '⚪ Servidor apagado'}
                </p>
              )}
              <ul className="sp-props-file-list">
                {configData.configs.map((config) => (
                  <li key={config.file}>
                    <button
                      type="button"
                      onClick={() => onSelectFile(config)}
                      className={`sp-props-file ${selectedConfigFile?.file === config.file ? 'is-active' : ''}`}
                    >
                      <span className="sp-props-file-icon">{getFileIcon(config.file)}</span>
                      <span className="sp-props-file-name">{config.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {selectedConfigFile && (
              <div className={`sp-props-editor ${isMobile && mobileTab === 'files' ? 'mobile-hidden' : ''}`}>
                <div className="sp-props-editor-head">
                  <div>
                    <h3>{getFileIcon(selectedConfigFile.file)} {selectedConfigFile.name}</h3>
                    <p className="sp-props-file-desc">{selectedConfigFile.description}</p>
                  </div>
                  <span className={`sp-pill ${isContentModified ? 'sp-pill--accent' : 'sp-pill--success'}`}>
                    {isContentModified ? '● Sin guardar' : '✓ Guardado'}
                  </span>
                </div>
                <textarea
                  className="sp-textarea"
                  value={editedContent}
                  onChange={(e) => onChangeContent(e.target.value)}
                  spellCheck={false}
                />
                <div className="sp-props-editor-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving || !isContentModified}
                    onClick={onSave}
                  >
                    {saving ? <><span className="spinner-small" /> Guardando…</> : <><Save size={14} /> Guardar</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="sp-empty"><p>No hay archivos de configuración disponibles.</p></div>
        )}
      </div>
    </ModalShell>
  );
}

// =========================================================================
// SessionInfoModal
// =========================================================================

interface SessionInfoModalProps {
  seconds: number;
  cost: number;
  formatTime: (s: number) => string;
  onClose: () => void;
}

export function SessionInfoModal({ seconds, cost, formatTime, onClose }: SessionInfoModalProps) {
  return (
    <ModalShell onClose={onClose} size="sm">
      <ModalHeader
        icon={<Activity size={18} />}
        title="Resumen de sesión"
        subtitle="Tu servidor se ha detenido"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        <div className="sp-stat-grid" style={{ marginBottom: '1rem' }}>
          <div className="sp-stat-tile">
            <span className="sp-stat-tile-label">⏱️ Tiempo de juego</span>
            <span className="sp-stat-tile-value">{formatTime(seconds)}</span>
          </div>
          <div className="sp-stat-tile">
            <span className="sp-stat-tile-label">💰 Costo</span>
            <span className="sp-stat-tile-value">
              ${cost > 0 ? cost.toFixed(2) : '0.00'}
            </span>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center' }}>
          Los créditos se descontarán automáticamente de tu balance.
        </p>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          <Check size={14} /> Aceptar
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// MapSelectionModal (Counter-Strike)
// =========================================================================

interface CSMap {
  name: string;
  image: string;
  description: string;
}

interface MapSelectionModalProps {
  maps: CSMap[];
  currentMap: string;
  changing: boolean;
  serverActive: boolean;
  onSelect: (name: string) => void;
  onClose: () => void;
}

export function MapSelectionModal({
  maps, currentMap, changing, serverActive, onSelect, onClose,
}: MapSelectionModalProps) {
  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<MapIcon size={18} />}
        title="Seleccionar mapa"
        subtitle="Cambia el mapa activo de tu servidor de Counter-Strike"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        {!serverActive && (
          <div className="sp-banner sp-banner--warning" style={{ borderRadius: 8, borderBottom: 'none', marginBottom: '1rem' }}>
            <span className="sp-banner-icon"><AlertCircle size={16} /></span>
            <span className="sp-banner-text">El servidor debe estar <strong>encendido</strong> para cambiar de mapa.</span>
          </div>
        )}
        <div className="sp-map-grid">
          {maps.map((m) => (
            <button
              key={m.name}
              type="button"
              onClick={() => onSelect(m.name)}
              disabled={changing || !serverActive}
              className={`sp-map-card ${currentMap === m.name ? 'is-active' : ''}`}
            >
              <div className="sp-map-image-wrap">
                <img src={m.image} alt={m.name} className="sp-map-image" />
              </div>
              <span className="sp-map-name">{m.name}</span>
              <span className="sp-map-desc">{m.description}</span>
            </button>
          ))}
        </div>
        {changing && (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <div className="spinner" />
            <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>Cambiando mapa…</p>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// =========================================================================
// PermissionsModal (collaborators)
// =========================================================================

interface PermissionsModalProps {
  permissionsList: any[];
  loading: boolean;
  adding: boolean;
  discordId: string;
  error: string | null;
  onDiscordIdChange: (id: string) => void;
  onAdd: (e: FormEvent) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

export function PermissionsModal({
  permissionsList, loading, adding, discordId, error,
  onDiscordIdChange, onAdd, onRemove, onClose,
}: PermissionsModalProps) {
  return (
    <ModalShell onClose={onClose} size="sm">
      <ModalHeader
        icon={<Users size={18} />}
        title="Colaboradores"
        subtitle="Personas que pueden encender y apagar este servidor"
        onClose={onClose}
      />
      <div className="sp-modal-body">
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Añade a otros usuarios para que puedan encender y apagar este servidor. El costo se descontará de su saldo cuando lo inicien.
        </p>

        <form onSubmit={onAdd} className="sp-form-row" style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="sp-input"
            placeholder="Discord User ID (ej: 123456789012345678)"
            value={discordId}
            onChange={(e) => onDiscordIdChange(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" disabled={adding || !discordId.trim()}>
            {adding ? <span className="spinner-small" /> : <Plus size={14} />}
            Añadir
          </button>
        </form>
        {error && (
          <p style={{ fontSize: '0.8rem', color: 'var(--error)', marginBottom: '0.75rem' }}>
            {error}
          </p>
        )}

        <h4 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', fontFamily: 'var(--font-mono, monospace)' }}>
          Colaboradores actuales
        </h4>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" /></div>
        ) : permissionsList.length === 0 ? (
          <div className="sp-empty">
            <Users size={32} />
            <p>No hay colaboradores aún</p>
          </div>
        ) : (
          <ul className="sp-player-list">
            {permissionsList.map((perm) => (
              <li key={perm.id} className="sp-player-row">
                <span className="sp-player-name">
                  <Users size={14} /> {perm.user?.username || 'Usuario'}
                </span>
                <button
                  type="button"
                  className="sp-copy-btn"
                  onClick={() => onRemove(perm.id)}
                  title="Eliminar acceso"
                  aria-label={`Eliminar acceso de ${perm.user?.username || 'usuario'}`}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}

// =========================================================================
// DeleteConfirmModal
// =========================================================================

interface DeleteConfirmModalProps {
  serverName: string;
  deleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function DeleteConfirmModal({ serverName, deleting, onConfirm, onClose }: DeleteConfirmModalProps) {
  return (
    <ModalShell onClose={onClose} size="sm">
      <div className="sp-modal-body sp-modal-body--center" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
        <div className="sp-delete-icon">
          <Trash2 size={48} />
        </div>
        <h2 className="sp-modal-title" style={{ marginTop: '1rem' }}>Eliminar servidor</h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
          ¿Estás seguro de que deseas eliminar <strong>{serverName}</strong>?
        </p>
        <p style={{ fontSize: '0.85rem', color: 'var(--error)', marginTop: '0.75rem' }}>
          Esta acción es permanente y no se puede deshacer.
        </p>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={deleting}>Cancelar</button>
        <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
          {deleting ? <><span className="spinner-small" /> Eliminando…</> : <><Trash2 size={14} /> Eliminar</>}
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// ImportantAlertModal
// =========================================================================

interface ImportantAlertModalProps {
  message: string;
  onClose: () => void;
}

export function ImportantAlertModal({ message, onClose }: ImportantAlertModalProps) {
  return (
    <ModalShell onClose={() => {}} size="sm">
      <div className="sp-modal-body sp-modal-body--center" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
        <div style={{ color: 'var(--warning)', marginBottom: '1rem' }}>
          <AlertCircle size={48} />
        </div>
        <p style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>{message}</p>
      </div>
      <div className="sp-modal-footer">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          <Check size={14} /> Aceptar
        </button>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// StatsModal (embeds StatsPanel)
// =========================================================================

interface StatsModalProps {
  serverId: string;
  isActive: boolean;
  onClose: () => void;
}

export function StatsModal({ serverId, isActive, onClose }: StatsModalProps) {
  return (
    <ModalShell onClose={onClose} size="md">
      <ModalHeader
        icon={<Activity size={18} />}
        title="Recursos del VPS"
        subtitle="Consumo en tiempo real"
        onClose={onClose}
      />
      <div className="sp-modal-body sp-modal-body--flush">
        <StatsPanel serverId={serverId} isActive={isActive} showHeader={false} />
      </div>
    </ModalShell>
  );
}

// =========================================================================
// ShutdownProgressModal — modal de "apagando" no cerrable
// =========================================================================

interface ShutdownProgressModalProps {
  game?: string;
}

/**
 * Modal bloqueante que aparece cuando el servidor empieza a apagarse.
 * Solo se cierra via SSE cuando llega `server.state_changed: stopped`.
 * No tiene botón de cerrar: el usuario no debe poder dismissarlo a mano.
 */
export function ShutdownProgressModal({ game }: ShutdownProgressModalProps) {
  const isMinecraft = game === 'Minecraft';
  const message = isMinecraft
    ? 'Guardando mundo y deteniendo el contenedor…'
    : 'Apagando el contenedor del juego…';

  return (
    <ModalShell onClose={() => { /* no cerrable */ }} size="sm">
      <div className="sp-modal-body" style={{ textAlign: 'center', padding: '2.5rem 1.5rem 2rem' }}>
        <div className="spinner sp-spinner-shutdown" />
        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Apagando servidor
        </h3>
        <p style={{ marginTop: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.4 }}>
          {message}
        </p>
        <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Este proceso puede tardar hasta 2 minutos. No cierres esta ventana.
        </p>
      </div>
    </ModalShell>
  );
}

// =========================================================================
// StartingProgressModal — modal de "encendiendo" no cerrable
// =========================================================================

interface StartingProgressModalProps {
  game?: string;
}

/**
 * Modal bloqueante que aparece cuando el servidor empieza a encenderse.
 * Solo se cierra via SSE cuando llega `server.state_changed: running`
 * (o via el timeout de seguridad de 3 minutos). No tiene botón de cerrar.
 * Coexiste con el banner "Servidor iniciando…" que se muestra arriba.
 */
export function StartingProgressModal({ game }: StartingProgressModalProps) {
  const isMinecraft = game === 'Minecraft';
  const message = isMinecraft
    ? 'Cargando mundo y levantando el contenedor…'
    : 'Levantando el contenedor del juego…';

  return (
    <ModalShell onClose={() => { /* no cerrable */ }} size="sm">
      <div className="sp-modal-body" style={{ textAlign: 'center', padding: '2.5rem 1.5rem 2rem' }}>
        <div className="spinner sp-spinner-shutdown" />
        <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Encendiendo servidor
        </h3>
        <p style={{ marginTop: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.4 }}>
          {message}
        </p>
        <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Este proceso puede tardar hasta 2 minutos. No cierres esta ventana.
        </p>
      </div>
    </ModalShell>
  );
}
